"use client";

import React from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { motion } from "framer-motion";
import { Flame, Skull, MessageSquare, Share2, ArrowUpRight } from "lucide-react";
import CdnImage from "@/components/CdnImage";
import UserHoverCard from "@/app/components/UserHoverCard";
import GradientAvatar from "@/app/components/GradientAvatar";
import PostEarningsBadge from "@/app/components/PostEarningsBadge";
import { useT } from "@/hooks/useT";
import { useMarketData } from "@/hooks/useMarketData";
import { fmtCents } from "@/lib/format-price";
import { decodeHTMLEntities } from "@/lib/sanitize";
import { formatUSD, timeAgo, displayTraderName, abbreviateOutcomeLabel } from "../../helpers/format";
import { CARD_BASE } from "../../helpers/styles";
import type { ActivityEvent } from "../../types";

// User Post Card — same chip aesthetic as the trade rows. Verified-trade
// posts get a left emerald accent + bought/sold/outcome badges; the
// market embed is the slim event card we use on WhaleTradeRow and the
// post detail page (no more PolymarketMarketCard duplication).
export default function UserPostRow({
  event,
  onVote,
}: {
  event: ActivityEvent;
  onVote: (eventId: string, voteType: number, sourceTable: string) => void;
}) {
  const t = useT();
  const router = useRouter();

  const tradeData = event.metadata?.trade_data || null;
  const hasVerifiedTrade = event.event_type === "user_post_trade" || event.metadata?.is_verified_trade === true || !!tradeData;
  const isSell = tradeData?.type === "SELL" || event.side === "SELL";
  const isBuy = !isSell;

  // Auto-trade copy detection + PnL extraction. The headline on close-
  // position posts is templated by the backend as:
  //   "Just closed my Yes position on \"...\" for +$X (Y%) profit! 🎉"
  //   "Closed my No position on \"...\" for $X (Y%). On to the next one 💪"
  // We hide the verbose paragraph and surface the PnL inline with the
  // trade badges instead. The sign math gets messy because the backend
  // sometimes emits "+$-0.00" / "(+-0.2%)" — we collapse duplicated signs.
  const rawHeadline = (event.headline || '').trim();
  const isAutoTradeCopy =
    /^(just\s+)?(bought|sold)\s+\d/i.test(rawHeadline) ||
    /^(just\s+)?closed\s+(my|a)\s+\w+\s+position/i.test(rawHeadline);
  const isClosePosition = /^(just\s+)?closed\s+(my|a)\s+\w+\s+position/i.test(rawHeadline);
  const collapseSigns = (s: string) => {
    const m = s.match(/[+-]/g);
    if (!m) return s.replace(/[+-]/g, '');
    const negatives = m.filter(c => c === '-').length;
    const bare = s.replace(/[+-]/g, '');
    return (negatives % 2 === 1 ? '-' : '') + bare;
  };
  let pnlUsd: number | null = null;
  let pnlPct: number | null = null;
  if (isClosePosition) {
    const pnlMatch = rawHeadline.match(/\$([+-]*\s*\d+(?:\.\d+)?)\s*\(([+-]*\s*\d+(?:\.\d+)?)%\)/i);
    if (pnlMatch) {
      const usd = parseFloat(collapseSigns(pnlMatch[1].replace(/\s+/g, '')));
      const pct = parseFloat(collapseSigns(pnlMatch[2].replace(/\s+/g, '')));
      if (Number.isFinite(usd)) pnlUsd = usd;
      if (Number.isFinite(pct)) pnlPct = pct;
    }
  }
  const postDisplayName = displayTraderName(
    event.metadata?.display_name || event.source_username || event.source_wallet
  ) || "Anonymous";

  const tradeOutcome = tradeData?.outcome || event.outcome || "";
  const isYesSide = tradeOutcome.toLowerCase().includes("yes");
  const rawPrice = tradeData?.price || event.price;
  const priceCents = rawPrice
    ? fmtCents(parseFloat(String(rawPrice)) * (tradeData?.price ? 0.01 : 1))
    : null;
  const tradeAmount = tradeData?.amount || event.size_usd;

  const marketHref = event.event_slug
    ? `/event/${event.event_slug}`
    : event.market_slug
      ? `/market/${event.market_slug}`
      : null;

  // Live market metadata — TanStack Query dedupes across rows on the same
  // slug and the backend caches 60s, so per-row cost is bounded.
  const { data: liveMarketData } = useMarketData(event.market_slug, {
    enabled: !!event.market_slug,
    source: 'polymarket',
  });
  // Title falls back to liveMarketData.question when the post row didn't
  // carry event.market_title (older posts where only the slug was
  // indexed) so we still render the slim card.
  const marketTitle = event.market_title || liveMarketData?.question || null;
  const marketImage = event.market_image || liveMarketData?.image || null;
  const marketVolume = liveMarketData?.volume ?? null;
  const marketLiquidity = liveMarketData?.liquidity ?? null;
  const marketEndDate = liveMarketData?.endDate ?? null;
  const marketClosed = liveMarketData?.closed === true;

  // Outcome buttons reconstruction (same pattern as WhaleTradeRow).
  let buttonOutcomes: string[] = [];
  let buttonPrices: number[] = [];
  let buttonLabels: string[] = [];
  if (rawPrice != null && tradeOutcome) {
    const rawP = Number(rawPrice);
    const tradedProb = rawP > 1 ? rawP / 100 : rawP;
    if (tradedProb >= 0 && tradedProb <= 1) {
      const outcomeLower = tradeOutcome.toLowerCase().trim();
      const titleHasVs = !!marketTitle && / vs\.? /i.test(marketTitle);
      if (titleHasVs) {
        const sides = marketTitle!.split(/ vs\.? /i).map(s => s.trim().replace(/\?$/, ""));
        if (sides.length === 2) {
          const idx = sides.findIndex(s => s.toLowerCase() === outcomeLower);
          if (idx >= 0) {
            buttonOutcomes = sides;
            buttonPrices = idx === 0 ? [tradedProb, 1 - tradedProb] : [1 - tradedProb, tradedProb];
          }
        }
      }
      if (buttonOutcomes.length === 0 && (outcomeLower === "yes" || outcomeLower === "no")) {
        buttonOutcomes = ["Yes", "No"];
        buttonPrices = outcomeLower === "yes" ? [tradedProb, 1 - tradedProb] : [1 - tradedProb, tradedProb];
      }
    }
  }
  // Fallback to live data when we can't reconstruct from the post.
  if (buttonOutcomes.length === 0 && liveMarketData?.outcomes && liveMarketData?.outcomePrices) {
    buttonOutcomes = liveMarketData.outcomes.map(String);
    buttonPrices = liveMarketData.outcomePrices.map(Number);
  }
  buttonLabels = buttonOutcomes.slice();
  if (buttonOutcomes.length === 2 && event.market_slug && / vs\.? /i.test(marketTitle || '')) {
    const parts = event.market_slug.split("-");
    const leaguePrefixes = new Set(["nba", "nfl", "mlb", "nhl", "epl", "laliga", "seriea", "ucl", "ncaaf", "ncaab", "f1", "atp", "wta", "mls", "mma", "ufc"]);
    const tokens: string[] = [];
    for (let i = 0; i < parts.length; i++) {
      const p = parts[i];
      if (i === 0 && leaguePrefixes.has(p)) continue;
      if (/^\d{2,4}$/.test(p)) break;
      if (p.length >= 2 && p.length <= 4 && /^[a-z]+$/i.test(p)) tokens.push(p.toUpperCase());
    }
    if (tokens.length >= 2) buttonLabels = [tokens[0], tokens[1]];
  }

  buttonLabels = buttonLabels.map(abbreviateOutcomeLabel);

  const handleCardClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('a') || target.closest('[role="button"]')) {
      return;
    }
    router.push(`/activity/${event.id}`);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
      onClick={handleCardClick}
      className={`${CARD_BASE}${hasVerifiedTrade ? " border-l-2 border-l-emerald-500/60 pl-2 -ml-0.5" : ""}`}
    >
      <div className="pt-3 pb-1.5">
        <div className="flex items-center gap-2.5">
          <UserHoverCard username={event.source_username || undefined} walletAddress={event.source_wallet || undefined}>
            <Link href={`/profile/${event.source_username || "anonymous"}`} prefetch={false} className="flex-shrink-0">
              {event.metadata?.user_image ? (
                <CdnImage src={event.metadata.user_image} alt={postDisplayName} width={32} height={32} sizes="32px" className="w-8 h-8 rounded-[5px] object-cover" />
              ) : (
                <GradientAvatar seed={postDisplayName} size={32} />
              )}
            </Link>
          </UserHoverCard>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline justify-between gap-2">
              <div className="flex items-baseline gap-1.5 min-w-0 flex-wrap">
                <UserHoverCard username={event.source_username || undefined} walletAddress={event.source_wallet || undefined}>
                  <Link href={`/profile/${event.source_username || "anonymous"}`} prefetch={false} className="text-sm font-bold text-gray-900 dark:text-white hover:underline truncate">
                    {postDisplayName}
                  </Link>
                </UserHoverCard>
                {event.source_username && (
                  <span className="text-xs text-gray-400">@{event.source_username}</span>
                )}
              </div>
              <span className="flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-[5px] bg-gray-100 dark:bg-white/[0.05] border border-gray-200 dark:border-white/[0.06] text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                {timeAgo(event.event_at, t)}
              </span>
            </div>
          </div>
        </div>

        {/* Post content — only render real user commentary. Auto-trade
            copy is collapsed into the badges/PnL chip below. */}
        {!isAutoTradeCopy && (
          <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap break-words mt-2 ml-10 line-clamp-3">
            {decodeHTMLEntities(event.headline || '').split(/(https?:\/\/[^\s<]+)/g).map((part, i) => {
              if (part.match(/^https?:\/\//)) {
                try {
                  const urlObj = new URL(part);
                  return (
                    <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-emerald-500 hover:underline">
                      {urlObj.hostname.replace('www.', '')}
                    </a>
                  );
                } catch { return part; }
              }
              return part;
            })}
          </p>
        )}

        {/* Verified trade context — badges in line, same pattern as the
            trade rows (bought/sold + size + outcome + price). */}
        {hasVerifiedTrade && (tradeData || (event.side && event.outcome)) && (
          <div className="flex items-baseline gap-1.5 flex-wrap mt-2">
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded-[5px] text-[11px] font-semibold ${
              isClosePosition
                ? 'bg-gray-500/10 text-gray-600 dark:text-gray-400'
                : isSell
                  ? 'bg-red-500/10 text-red-600 dark:text-red-400'
                  : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
            }`}>
              {isClosePosition ? t("activity.action.closed") : isSell ? t("activityDetail.sold") : t("activityDetail.bought")}
            </span>
            {tradeAmount != null && !isClosePosition && (
              <span className="text-sm font-bold text-gray-900 dark:text-white tabular-nums">
                ${parseFloat(String(tradeAmount)).toFixed(0)}
              </span>
            )}
            {tradeOutcome && (
              <span className={`inline-flex items-center px-1.5 py-0.5 rounded-[5px] text-[11px] font-semibold ${
                isYesSide
                  ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                  : 'bg-red-500/10 text-red-600 dark:text-red-400'
              }`}>
                {tradeOutcome}
              </span>
            )}
            {priceCents && !isClosePosition && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                @ <span className="font-semibold text-gray-900 dark:text-white">{priceCents}¢</span>
              </span>
            )}
            {isClosePosition && (pnlUsd != null || pnlPct != null) && (() => {
              const isProfit = (pnlUsd ?? pnlPct ?? 0) > 0;
              const isLoss = (pnlUsd ?? pnlPct ?? 0) < 0;
              const tone = isProfit
                ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                : isLoss
                  ? 'bg-red-500/10 text-red-600 dark:text-red-400'
                  : 'bg-gray-500/10 text-gray-600 dark:text-gray-400';
              const sign = isProfit ? '+' : '';
              return (
                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-[5px] text-[11px] font-semibold tabular-nums ${tone}`}>
                  {pnlUsd != null && (
                    <span>{sign}${Math.abs(pnlUsd).toFixed(2)}</span>
                  )}
                  {pnlPct != null && (
                    <span className="opacity-80">({sign}{Math.abs(pnlPct).toFixed(1)}%)</span>
                  )}
                </span>
              );
            })()}
          </div>
        )}

        {/* Slim event card — same pattern as WhaleTradeRow + detail page. */}
        {marketHref && marketTitle && (
          <Link
            href={marketHref}
            prefetch={false}
            onClick={(e) => e.stopPropagation()}
            className="block mt-2.5 p-2.5 rounded-[5px] bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.06] hover:bg-gray-100 dark:hover:bg-white/[0.06] hover:border-gray-300 dark:hover:border-white/[0.1] transition-colors"
          >
            <div className="flex items-center gap-2.5">
              {marketImage && (
                <div className="w-8 h-8 rounded-[5px] overflow-hidden flex-shrink-0 bg-gray-200 dark:bg-white/[0.06]">
                  <CdnImage src={marketImage} alt={marketTitle} width={32} height={32} sizes="32px" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                  {marketTitle}
                </h3>
                {(marketVolume != null || marketLiquidity != null || marketEndDate) && (() => {
                  const src = (event.metadata?.source as string) || 'polymarket';
                  const sourceLogo =
                    src === 'kalshi' ? '/logos/kalshi/k-icon.svg'
                    : src === 'hyperliquid' ? '/logos/hyperliquid/symbol-mint.svg'
                    : src === 'predict' ? ''
                    : '/logos/polymarket/icon-white.png';
                  const sourceBg =
                    src === 'kalshi' ? 'bg-emerald-500/15'
                    : src === 'predict' ? 'bg-violet-500/15'
                    : src === 'hyperliquid' ? 'bg-cyan-500/15'
                    : 'bg-blue-500/15';
                  return (
                    <div className="flex items-center gap-1.5 mt-0.5 text-[10px] text-gray-500 dark:text-gray-400 flex-wrap">
                      <span className={`inline-flex items-center justify-center w-5 h-5 rounded-[4px] flex-shrink-0 ${sourceBg}`}>
                        {sourceLogo ? (
                          <img src={sourceLogo} alt={src} className="w-3.5 h-3.5 object-contain" />
                        ) : (
                          <span className="text-[10px] font-bold text-violet-600 dark:text-violet-400">P</span>
                        )}
                      </span>
                      {marketClosed && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-[5px] bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 font-semibold whitespace-nowrap">
                          {t("activity.badge.marketClosed")}
                        </span>
                      )}
                      {marketVolume != null && marketVolume > 0 && (
                        <>
                          <span className="text-gray-300 dark:text-gray-600">·</span>
                          <span className="whitespace-nowrap tabular-nums">{formatUSD(marketVolume)} vol</span>
                        </>
                      )}
                      {marketLiquidity != null && marketLiquidity > 0 && (
                        <>
                          <span className="text-gray-300 dark:text-gray-600">·</span>
                          <span className="whitespace-nowrap tabular-nums">{formatUSD(marketLiquidity)} liq</span>
                        </>
                      )}
                      {marketEndDate && (
                        <>
                          <span className="text-gray-300 dark:text-gray-600">·</span>
                          <span className="whitespace-nowrap">
                            {new Date(marketEndDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                          </span>
                        </>
                      )}
                    </div>
                  );
                })()}
              </div>
              <ArrowUpRight className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
            </div>
            {buttonOutcomes.length === 2 && (
              <div className="flex gap-1.5 mt-2">
                {[0, 1].map((i) => {
                  const isEmerald = i === 0;
                  const borderColor = isEmerald ? 'border-emerald-500/30 hover:border-emerald-500/60' : 'border-red-500/30 hover:border-red-500/60';
                  const bgColor = isEmerald ? 'bg-emerald-500/5 hover:bg-emerald-500/10' : 'bg-red-500/5 hover:bg-red-500/10';
                  const textColor = isEmerald ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400';
                  return (
                    <Link
                      key={buttonOutcomes[i]}
                      href={`${marketHref}?outcome=${encodeURIComponent(buttonOutcomes[i].toLowerCase())}`}
                      prefetch={false}
                      onClick={(e) => e.stopPropagation()}
                      className={`flex-1 flex items-center justify-between px-2.5 py-1.5 rounded-[5px] border transition-colors ${borderColor} ${bgColor}`}
                    >
                      <span className={`text-[10px] font-bold uppercase tracking-wide truncate ${textColor}`}>
                        {buttonLabels[i]}
                      </span>
                      <span className="text-xs font-bold text-gray-900 dark:text-white tabular-nums flex-shrink-0 ml-2">
                        {(buttonPrices[i] * 100).toFixed(1)}¢
                      </span>
                    </Link>
                  );
                })}
              </div>
            )}
          </Link>
        )}

        {/* Earnings social proof */}
        {event.post_earnings ? (
          <div className="mt-2 ml-10">
            <PostEarningsBadge
              earnings={event.post_earnings}
              username={event.source_username}
              compact
            />
          </div>
        ) : null}

        {/* Actions — flush left at the card edge; votes + comments on the
            left, Share on X flush right. Extra top margin so the action
            row breathes away from the post text / event card above. */}
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-1.5">
            {/* 2026-05-22 (Pika): added explicit aria/title labels
                on every action icon. The audit pointed out that the
                row read as "four anonymous mini-icons" without any
                way to know which one was like / dislike / comment
                from a glance. */}
            <button
              onClick={() => onVote(event.id, 1, event.source_table || 'post')}
              aria-label={t("action.like")}
              title={t("action.like")}
              className={`flex items-center gap-1 px-1.5 py-1 rounded-[5px] text-xs transition-colors ${
                event.user_vote === 1 ? 'text-orange-500 bg-orange-500/10' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-white/[0.06] hover:text-orange-500'
              }`}
            >
              <Flame className="w-3.5 h-3.5" />
              <span>{event.likes || event.reactions?.fire || 0}</span>
            </button>
            <button
              onClick={() => onVote(event.id, -1, event.source_table || 'post')}
              aria-label={t("action.dislike")}
              title={t("action.dislike")}
              className={`flex items-center gap-1 px-1.5 py-1 rounded-[5px] text-xs transition-colors ${
                event.user_vote === -1 ? 'text-red-500 bg-red-500/10' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-white/[0.06] hover:text-red-500'
              }`}
            >
              <Skull className="w-3.5 h-3.5" />
              <span>{event.dislikes || event.reactions?.skull || 0}</span>
            </button>
            <Link
              href={`/post/${event.id}`}
              aria-label={t("action.comment")}
              title={t("action.comment")}
              className="flex items-center gap-1 px-1.5 py-1 rounded-[5px] text-xs text-gray-400 hover:bg-gray-100 dark:hover:bg-white/[0.06] hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <MessageSquare className="w-3.5 h-3.5" />
              {event.copy_count > 0 && <span>{event.copy_count}</span>}
            </Link>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              const postUrl = `${typeof window !== 'undefined' ? window.location.origin : 'https://www.frenflow.com'}/activity/${event.id}`;
              const intent = `https://x.com/intent/tweet?url=${encodeURIComponent(postUrl)}`;
              window.open(intent, '_blank', 'noopener,noreferrer');
            }}
            className="flex items-center gap-1 px-1.5 py-1 rounded-[5px] text-xs text-gray-400 hover:bg-gray-100 dark:hover:bg-white/[0.06] hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            aria-label={t("action.shareOnX")}
            title={t("action.shareOnX")}
          >
            <Share2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </motion.div>
  );
}
